import React from 'react';
import { Field, useFormikContext } from 'formik';
import { Typography, InputLabel, FormControl, FormHelperText, Tooltip } from '@mui/material';
import {
    StepWrapper,
    StyledCardSection,
    DatePicker,
    Select,
    TextField,
    StepperControls,
} from '@fnol/library/src/components';
import { useApi } from '@fnol/library/src/hooks';
import { checkPolicyNumber, common, fiveCalendarYearsFromNow, prepareHours } from '@fnol/library/src/lib';
import { translations, question15MaxLength } from '../../lib/consts';
import { checkVehicleRegistration, prepareMinutes } from '../../lib/helpers';
import { getPolicyDetails } from '../../rpc/actions';
import './policy.css';

type GenericObject = { [key: string]: any };

type PolicyDetailScreenProps = {
    navigator: GenericObject[];
    values: GenericObject[];
};

interface FormData {
    policyOrRegistrationNumber: string;
    policyNumber: string;
    registrationNumber: string;
    dateOfOccurance: string;
    hourOfOccurance: string;
    minuteOfOccurance: string;
}

const PolicyDetailScreen = ({ navigator }: PolicyDetailScreenProps) => {
    const [currentStep, { goForward }] = navigator;
    const { values, setFieldValue, setErrors, setTouched, errors, touched } = useFormikContext<FormData>();

    const toDate = (date: string) => {
        return new Date(date);
    };
    const preparedHours = prepareHours();
    const preparedMinutes = prepareMinutes();

    const savePolicy = (res: GenericObject) => {
        if (res.data.poruka === '') {
            setFieldValue('policyNumber', res.data.brojPolise);
            setFieldValue('registrationNumber', res.data.regOznaka);
            setFieldValue('reportedClaims', res.data.prijavljeneStete);
            setFieldValue('policyUCN', res.data.jmbg);
            setFieldValue('chassisNumberByPolicy', res.data.brSasije);
            goForward();
        }
    };

    const { response: policyDetails, fetchMethod: fetchPolicyDetails } = useApi(
        () => getPolicyDetails(data),
        savePolicy,
    );
    const data = {
        BrojPolise: values.policyNumber,
        RegistarskiBroj: values.registrationNumber,
        DatumNastanka:
            values.dateOfOccurance !== ''
                ? `${toDate(values.dateOfOccurance).getFullYear()}-${(
                      '0' +
                      (toDate(values.dateOfOccurance).getMonth() + 1)
                  ).slice(-2)}-${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}`
                : values.dateOfOccurance,
        VremeNastanka: `${values.hourOfOccurance}:${values.minuteOfOccurance}`,
    };

    const handleNext = () => {
        let valid = true;
        setTouched(
            {
                policyOrRegistrationNumber: true,
                dateOfOccurance: true,
                hourOfOccurance: true,
                minuteOfOccurance: true,
            },
            false,
        );
        const existingErrors: GenericObject = {};
        const escapedPolicyOrRegistrationNumber = values.policyOrRegistrationNumber.replace(/\s/g, '');
        if (escapedPolicyOrRegistrationNumber === '') {
            existingErrors.policyOrRegistrationNumber =
                translations.validationMessages.POLICY_OR_REGISTRATION_NUMBER_REQUIRED;
            valid = false;
        } else if (
            !checkPolicyNumber(escapedPolicyOrRegistrationNumber) &&
            !checkVehicleRegistration(escapedPolicyOrRegistrationNumber)
        ) {
            existingErrors.policyOrRegistrationNumber =
                translations.validationMessages.POLICY_OR_REGISTRATION_NUMBER_INVALID_FORMAT;
            valid = false;
        }
        if (values.dateOfOccurance === '' || values.dateOfOccurance === null) {
            existingErrors.dateOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.hourOfOccurance === '') {
            existingErrors.hourOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.minuteOfOccurance === '') {
            existingErrors.minuteOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        setErrors(existingErrors);
        if (valid) {
            if (escapedPolicyOrRegistrationNumber !== '') {
                if (checkPolicyNumber(escapedPolicyOrRegistrationNumber)) {
                    setFieldValue('policyNumber', escapedPolicyOrRegistrationNumber);
                    setFieldValue('registrationNumber', '');
                    data.BrojPolise = escapedPolicyOrRegistrationNumber;
                    data.RegistarskiBroj = '';
                } else if (checkVehicleRegistration(escapedPolicyOrRegistrationNumber)) {
                    setFieldValue('policyNumber', '');
                    setFieldValue('registrationNumber', escapedPolicyOrRegistrationNumber);
                    data.BrojPolise = '';
                    data.RegistarskiBroj = escapedPolicyOrRegistrationNumber;
                }
            }
            fetchPolicyDetails();
        }
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.VEHICLE_TO_BLAME}
                </Typography>

                <Tooltip
                    title={
                        <>
                            <p>{translations.tooltips.POLICY_NUMBER_TOOLTIP}</p>
                            <p>{translations.tooltips.REGISTRATION_NUMBER_TOOLTIP}</p>
                        </>
                    }
                    placement="top-start"
                >
                    <FormControl error={!!(touched.policyOrRegistrationNumber && errors.policyOrRegistrationNumber)}>
                        <Field
                            name="policyOrRegistrationNumber"
                            label={translations.POLICY_OR_REGISTRATION_NUMBER}
                            as={TextField}
                            inputProps={{
                                maxLength: question15MaxLength,
                            }}
                            error={!!(touched.policyOrRegistrationNumber && errors.policyOrRegistrationNumber)}
                            required
                        />
                        {touched.policyOrRegistrationNumber && errors.policyOrRegistrationNumber ? (
                            <FormHelperText error={true}>
                                <>{errors.policyOrRegistrationNumber}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </Tooltip>
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.TIME_OF_OCCURANCE}
                </Typography>

                <FormControl error={!!(touched.dateOfOccurance && errors.dateOfOccurance)} className="policyDatePicker">
                    <Field
                        name="dateOfOccurance"
                        label={translations.DATE}
                        as={DatePicker}
                        disableFuture
                        minDate={fiveCalendarYearsFromNow}
                        error={!!(touched.dateOfOccurance && errors.dateOfOccurance)}
                    />
                    {touched.dateOfOccurance && errors.dateOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.dateOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>

                <FormControl error={!!(touched.hourOfOccurance && errors.hourOfOccurance)}>
                    <InputLabel id="hourOfOccurance">{translations.HOUR}</InputLabel>
                    <Field name="hourOfOccurance" label={translations.HOUR} items={preparedHours || []} as={Select} />
                    {touched.hourOfOccurance && errors.hourOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.hourOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>

                <FormControl error={!!(touched.minuteOfOccurance && errors.minuteOfOccurance)}>
                    <InputLabel id="minuteOfOccurance">{translations.MINUTE}</InputLabel>
                    <Field
                        name="minuteOfOccurance"
                        label={translations.MINUTE}
                        items={preparedMinutes || []}
                        as={Select}
                    />
                    {touched.minuteOfOccurance && errors.minuteOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.minuteOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </StyledCardSection>
            {policyDetails.isSuccess && policyDetails?.data?.data.poruka ? (
                <FormHelperText error={true}>
                    <>{policyDetails?.data?.data.poruka}</>
                </FormHelperText>
            ) : (
                <></>
            )}
            <StepperControls onNext={handleNext} onNextLabel={common.NEXT} onPreviousLabel={common.PREVIOUS} />
        </StepWrapper>
    );
};

export default PolicyDetailScreen;
